import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, RotateCcw, Loader2, AlertTriangle, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorbeilleV2 = ({ isAdmin }) => {
  const [activeTab, setActiveTab] = useState("dossiers");
  const [dossiers, setDossiers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteType, setConfirmDeleteType] = useState("dossier");
  const navigate = useNavigate();

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, aRes] = await Promise.all([
        axios.get(`${API}/corbeille`),
        axios.get(`${API}/corbeille/appointments`),
      ]);
      setDossiers(dRes.data.dossiers || []);
      setAppointments(aRes.data.appointments || []);
    } catch {
      toast.error("Error al cargar la papelera");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/corbeille/${id}/restore`);
      toast.success("Dossier restaurado");
      fetchTrash();
    } catch {
      toast.error("Error al restaurar");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreAppointment = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/corbeille/appointments/${id}/restore`);
      toast.success("Cita restaurada");
      fetchTrash();
    } catch {
      toast.error("Error al restaurar");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (id) => {
    setActionLoading(id);
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/corbeille/${id}`);
      toast.success("Dossier eliminado permanentemente");
      fetchTrash();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDeleteAppointment = async (id) => {
    setActionLoading(id);
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/corbeille/appointments/${id}`);
      toast.success("Cita eliminada permanentemente");
      fetchTrash();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmptyTrash = async () => {
    setConfirmEmpty(false);
    setActionLoading("all");
    try {
      const res = await axios.delete(`${API}/corbeille`);
      toast.success(res.data.message);
      fetchTrash();
    } catch {
      toast.error("Error al vaciar la papelera");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return isoStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const currentItems = activeTab === "dossiers" ? dossiers : appointments;
  const totalItems = dossiers.length + appointments.length;

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="corbeille-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Papelera</h1>
          <p className="text-sm text-slate-500 mt-1">{totalItems} elemento{totalItems !== 1 ? "s" : ""} en la papelera</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-100 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setActiveTab("dossiers")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === "dossiers" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-dossiers"
        >
          Dossiers ({dossiers.length})
        </button>
        <button
          onClick={() => setActiveTab("planning")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === "planning" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-planning"
        >
          <Calendar className="w-3.5 h-3.5" />
          Planning ({appointments.length})
        </button>
      </div>

      {currentItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Trash2 className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-400 text-sm">
              {activeTab === "dossiers" ? "No hay dossiers en la papelera" : "No hay citas en la papelera"}
            </p>
          </CardContent>
        </Card>
      ) : activeTab === "dossiers" ? (
        <div className="space-y-2">
          {dossiers.map((d) => {
            const nom = d.donnees?.identite?.nom_proprietaire || "Sin nombre";
            const ref = d.reference || d.id?.slice(0, 8);
            const isLoading = actionLoading === d.id;
            return (
              <Card key={d.id} className="hover:shadow-sm transition-shadow" data-testid={`trash-item-${d.id}`}>
                <CardContent className="flex items-center justify-between py-3 px-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{nom}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span>{ref}</span>
                      {d.deleted_at && <span>Eliminado: {formatDate(d.deleted_at)}</span>}
                      {d.donnees?.identite?.ville && <span>{d.donnees.identite.ville}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => handleRestore(d.id)} disabled={isLoading} data-testid={`restore-${d.id}`}>
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      Restaurar
                    </Button>
                    {isAdmin && (
                    <Button variant="destructive" size="sm" onClick={() => { setConfirmDelete(d.id); setConfirmDeleteType("dossier"); }} disabled={isLoading} data-testid={`delete-permanent-${d.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                    </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {appointments.map((a) => {
            const nom = a.client_name || "Sin nombre";
            const date = a.date || "";
            const time = a.start_time || "";
            const city = a.city || "";
            const isLoading = actionLoading === a.id;
            return (
              <Card key={a.id} className="hover:shadow-sm transition-shadow" data-testid={`trash-appt-${a.id}`}>
                <CardContent className="flex items-center justify-between py-3 px-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{nom}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      {date && <span>{date} {time}</span>}
                      {city && <span>{city}</span>}
                      {a.vendeur && <span className="text-blue-500">{a.vendeur}</span>}
                      {a.deleted_at && <span>Eliminado: {formatDate(a.deleted_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => handleRestoreAppointment(a.id)} disabled={isLoading} data-testid={`restore-appt-${a.id}`}>
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      Restaurar
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => { setConfirmDelete(a.id); setConfirmDeleteType("appointment"); }} disabled={isLoading} data-testid={`delete-permanent-appt-${a.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirm single permanent delete */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Eliminación permanente
            </DialogTitle>
            <DialogDescription>
              {confirmDeleteType === "dossier"
                ? "Este dossier y todos sus archivos serán eliminados permanentemente. Esta acción es irreversible."
                : "Esta cita será eliminada permanentemente. Esta acción es irreversible."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => confirmDeleteType === "dossier" ? handlePermanentDelete(confirmDelete) : handlePermanentDeleteAppointment(confirmDelete)} data-testid="confirm-delete-btn">
              Eliminar permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CorbeilleV2;
